import React, { Suspense } from 'react';
import {
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import styled from 'styled-components';
import {
  colorBackgroundBody,
  colorBorder,
  colorWhite,
} from '@/utils/palette';
import { paddingLeft } from '@/utils/directions';
import {
  getDefaultAuthenticatedRoute,
  isAuthenticated,
} from '@/services/authService';
import Layout from '../Layout/index';
import MainWrapper from './MainWrapper';
import { routeLoaders } from './routeLoaders';

const LogIn = React.lazy(() => import('../LogIn/index'));
const Home = React.lazy(routeLoaders['/pages/inicio']);
const FormPadrao = React.lazy(routeLoaders['/pages/form-padrao']);
const Clientes = React.lazy(routeLoaders['/pages/clientes']);
const Propostas = React.lazy(routeLoaders['/pages/propostas']);
const Freelancers = React.lazy(routeLoaders['/pages/freelancers']);
const FreelancersAtividades = React.lazy(routeLoaders['/pages/lancar-atividade']);
const FreelancersAtividadesAtivas = React.lazy(routeLoaders['/pages/minhas-atividades']);
const FreelancersAtividadesLote = React.lazy(routeLoaders['/pages/lancar-em-lote']);
const FreelancerMinhasAtividades = React.lazy(routeLoaders['/pages/minhas-atividades-freelancer']);
const FreelancerMetricas = React.lazy(routeLoaders['/pages/metricas-freelancer']);
const TwoFactorAuth = React.lazy(routeLoaders['/pages/2fa']);
const CreateUser = React.lazy(routeLoaders['/pages/criar-usuario']);
const CreateFreelancer = React.lazy(routeLoaders['/pages/cadastro-freelancer']);
const ListUsers = React.lazy(routeLoaders['/pages/lista-de-usuarios']);
const Profile = React.lazy(routeLoaders['/pages/perfil']);
const Reports = React.lazy(routeLoaders['/pages/relatorios']);
const ContasPendentes = React.lazy(routeLoaders['/pages/contas-pendentes']);
const ContasAprovadas = React.lazy(routeLoaders['/pages/contas-aprovadas']);
const ContasRejeitadas = React.lazy(routeLoaders['/pages/contas-rejeitadas']);
const SupportTeam = React.lazy(routeLoaders['/pages/time-de-suporte']);

const Pages = () => (
  <Switch>
    <Route path="/pages/inicio" component={Home} />
    <Route path="/pages/contas-pendentes" component={ContasPendentes} />
    <Route path="/pages/contas-aprovadas" component={ContasAprovadas} />
    <Route path="/pages/contas-rejeitadas" component={ContasRejeitadas} />
    <Route path="/pages/clientes" component={Clientes} />
    <Route path="/pages/propostas" component={Propostas} />
    <Route path="/pages/freelancers" component={Freelancers} />
    <Route path="/pages/lancar-atividade" component={FreelancersAtividades} />
    <Route path="/pages/minhas-atividades" component={FreelancersAtividadesAtivas} />
    <Route
      path="/pages/minhas-atividades-freelancer"
      component={FreelancerMinhasAtividades}
    />
    <Route
      path="/pages/metricas-freelancer"
      component={FreelancerMetricas}
    />
    <Route path="/pages/lancar-em-lote" component={FreelancersAtividadesLote} />
    <Route path="/pages/2fa" component={TwoFactorAuth} />
    <Route path="/pages/criar-usuario" component={CreateUser} />
    <Route path="/pages/cadastro-freelancer" component={CreateFreelancer} />
    <Route path="/pages/lista-de-usuarios" component={ListUsers} />
    <Route path="/pages/perfil" component={Profile} />
    <Route path="/pages/relatorios" component={Reports} />
    <Route path="/pages/form-padrao" component={FormPadrao} />
    <Route path="/pages/time-de-suporte" component={SupportTeam} />
    <Redirect to={getDefaultAuthenticatedRoute()} />
  </Switch>
);

const WrappedRoutes = () => {
  if (!isAuthenticated()) {
    return <Redirect to="/log_in?reason=session-expired" />;
  }

  return (
    <div>
      <Layout />
      <ContainerWrap>
        <Route exact path="/propostas" render={() => <Redirect to="/pages/propostas" />} />
        <Route
          exact
          path="/freelancers/cadastros"
          render={() => <Redirect to="/pages/freelancers" />}
        />
        <Route
          exact
          path="/freelancers/lancar-atividade"
          render={() => <Redirect to="/pages/lancar-atividade" />}
        />
        <Route
          exact
          path="/freelancers/lancaratividade"
          render={() => <Redirect to="/pages/lancar-atividade" />}
        />
        <Route
          exact
          path="/freelancers/atividadesativas"
          render={() => <Redirect to="/pages/minhas-atividades" />}
        />
        <Route
          exact
          path="/freelancers/atividades-ativas"
          render={() => <Redirect to="/pages/minhas-atividades" />}
        />
        <Route
          exact
          path="/minhas-atividades"
          render={() => <Redirect to="/pages/minhas-atividades" />}
        />
        <Route
          exact
          path="/minhas-atividades-admin"
          render={() => <Redirect to="/pages/minhas-atividades" />}
        />
        <Route
          exact
          path="/minhas-atividades-freelancer"
          render={() => <Redirect to="/pages/minhas-atividades-freelancer" />}
        />
        <Route
          exact
          path="/metricas-freelancer"
          render={() => <Redirect to="/pages/metricas-freelancer" />}
        />
        <Route
          exact
          path="/relatorios"
          render={() => <Redirect to="/pages/relatorios" />}
        />
        <Route
          exact
          path="/relatorios/atividades"
          render={() => <Redirect to="/pages/relatorios" />}
        />
        <Route
          exact
          path="/relatorios/relatorio-operacional-financeiro-de-atividades"
          render={() => <Redirect to="/pages/relatorios" />}
        />
        <Route
          exact
          path="/freelancers/lancaremlote"
          render={() => <Redirect to="/pages/lancar-em-lote" />}
        />
        <Route
          exact
          path="/autenticacao/validar"
          render={() => <Redirect to="/pages/2fa" />}
        />
        <Suspense fallback={<ContentRouteFallback />}>
          <Route path="/pages" component={Pages} />
        </Suspense>
      </ContainerWrap>
    </div>
  );
};

const PublicLogInRoute = () => {
  if (isAuthenticated()) {
    return <Redirect to={getDefaultAuthenticatedRoute()} />;
  }

  return (
    <Suspense fallback={<RouteFallback>Carregando página...</RouteFallback>}>
      <LogIn />
    </Suspense>
  );
};

const Router = () => (
  <MainWrapper>
    <main>
      <Switch>
        <Route exact path="/" component={PublicLogInRoute} />
        <Route exact path="/log_in" component={PublicLogInRoute} />
        <Route path="/" component={WrappedRoutes} />
      </Switch>
    </main>
  </MainWrapper>
);

export default Router;

// region STYLES

const ContainerWrap = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  transition: padding-left 0.3s ease;

  ${paddingLeft}: 0;

  background: ${colorBackgroundBody};

  @media screen and (min-width: 576px) {
    ${paddingLeft}: 276px;
  }

  @media screen and (max-width: 576px) {
    padding-top: 90px;
  }
`;

const RouteFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: #64748b;
  font-size: 14px;
`;

const ContentRouteFallback = () => (
  <ContentFallback aria-label="Carregando conteúdo" role="status">
    <SkeletonTitle />
    <SkeletonSubtitle />
    <SkeletonGrid>
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </SkeletonGrid>
    <SkeletonPanel />
  </ContentFallback>
);

const ContentFallback = styled.div`
  min-height: calc(100vh - 90px);
  padding: 30px;
  background: ${colorBackgroundBody};
`;

const Skeleton = styled.div`
  animation: shimmer 1.4s ease-in-out infinite;
  background: linear-gradient(90deg, ${colorWhite} 25%, #f1f5f9 50%, ${colorWhite} 75%);
  background-size: 200% 100%;
  border: 1px solid ${colorBorder};
  border-radius: 8px;

  @keyframes shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }
`;

const SkeletonTitle = styled(Skeleton)`
  width: min(280px, 65%);
  height: 28px;
`;

const SkeletonSubtitle = styled(Skeleton)`
  width: min(420px, 85%);
  height: 16px;
  margin-top: 12px;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 28px;

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonCard = styled(Skeleton)`
  height: 128px;
`;

const SkeletonPanel = styled(Skeleton)`
  height: 280px;
  margin-top: 20px;
`;

// endregion
